/**
 * Text Column Factory for Fluent UI DataGrid
 *
 * Creates standardized text columns with automatic truncation and optional sorting.
 * Eliminates boilerplate for common text column patterns across all DataGrid tables.
 *
 * @example
 * ```tsx
 * const columns = [
 *   createTextColumn<User>({
 *     columnId: 'email',
 *     headerText: 'Email',
 *     getValue: (user) => user.email,
 *     sortable: true,
 *   }),
 * ];
 * ```
 */

import {
  TableCellLayout,
  TableColumnDefinition,
  createTableColumn,
  Text,
} from '@fluentui/react-components';

interface CreateTextColumnOptions<TItem> {
  /**
   * Unique identifier for the column
   */
  columnId: string;

  /**
   * Text to display in column header
   */
  headerText: string;

  /**
   * Function to extract the text value from the item
   */
  getValue: (item: TItem) => string;

  /**
   * Whether the column should be sortable
   * When true, uses localeCompare for string sorting
   */
  sortable?: boolean;

  /**
   * Whether to wrap text content (default: true)
   * When false, text will truncate with ellipsis
   */
  wrap?: boolean;
}

/**
 * Creates a text column with optional word wrapping and sorting
 *
 * @param options Configuration options for the text column
 * @returns TableColumnDefinition configured with wrapping/truncation and optional sorting
 */
export function createTextColumn<TItem>(
  options: CreateTextColumnOptions<TItem>
): TableColumnDefinition<TItem> {
  const { columnId, headerText, getValue, sortable = false, wrap = true } = options;

  return createTableColumn<TItem>({
    columnId,
    renderHeaderCell: () => <Text weight="semibold">{headerText}</Text>,
    renderCell: (item: TItem) => (
      <TableCellLayout truncate={!wrap}>
        {getValue(item)}
      </TableCellLayout>
    ),
    ...(sortable && {
      compare: (a: TItem, b: TItem) => getValue(a).localeCompare(getValue(b)),
    }),
  });
}
